import { ApiResponse } from "../models/api-response";
import { callApi } from "./api.service";
import type { BlogPostViewModel } from "../models/blog";

const strapiUrl = import.meta.env.VITE_STRAPI_URL;

interface StrapiMediaFormat {
  url?: string;
}

interface StrapiMedia {
  url?: string;
  alternativeText?: string | null;
  formats?: {
    small?: StrapiMediaFormat;
    thumbnail?: StrapiMediaFormat;
  };
}

interface StrapiTag {
  id: number;
  name: string;
  slug?: string;
}

interface StrapiAuthor {
  name: string;
  role?: string;
  bio?: string;
  avatar?: StrapiMedia | null;
}

/** Raw Strapi blog-post entry (flat REST shape used by our API layer). */
export interface StrapiBlogPost {
  id: number;
  documentId: string;
  title: string;
  slug: string;
  excerpt?: string;
  body: string;
  createdAt: string;
  updatedAt: string;
  publishedAt?: string;
  coverImage?: StrapiMedia | null;
  coverImageAlt?: string;
  author?: StrapiAuthor | null;
  tags?: StrapiTag[] | null;
  faq?: Array<{ id: number; question: string; answer: string }> | null;
}

interface StrapiBlogPostsResponse {
  data: StrapiBlogPost[];
  meta: {
    pagination: {
      page: number;
      pageSize: number;
      pageCount: number;
      total: number;
    };
  };
}

export type BlogPost = BlogPostViewModel;

export interface BlogPostsResponse {
  items: BlogPost[];
  total: number;
  page: number;
  pageSize: number;
  pageCount: number;
}

function toAbsoluteUrl(url?: string): string | undefined {
  if (!url) return undefined;
  if (url.startsWith("http://") || url.startsWith("https://")) return url;
  return `${strapiUrl}${url}`;
}

export function mapStrapiPostToModel(post: StrapiBlogPost): BlogPost {
  const tags = (post.tags || []).map((tag) => ({
    id: String(tag.id),
    name: tag.name,
    slug: tag.slug,
  }));

  return {
    id: post.id,
    documentId: post.documentId ?? String(post.id),
    title: post.title,
    slug: post.slug,
    excerpt: post.excerpt || "",
    body: post.body,
    createdAt: post.createdAt,
    updatedAt: post.updatedAt,
    publishedAt: post.publishedAt,
    coverImageUrl: toAbsoluteUrl(post.coverImage?.url),
    coverImageSmallUrl: toAbsoluteUrl(post.coverImage?.formats?.small?.url),
    coverImageAlt: post.coverImageAlt || post.coverImage?.alternativeText || post.title,
    author: post.author
      ? {
          name: post.author.name,
          role: post.author.role,
          bio: post.author.bio,
          avatarUrl: toAbsoluteUrl(post.author.avatar?.formats?.thumbnail?.url) || toAbsoluteUrl(post.author.avatar?.url),
        }
      : undefined,
    tags,
    faq: (post.faq ?? []).map(({ question, answer }) => ({
      question,
      answer,
    })),
  };
}

export async function getBlogPosts(
  page: number = 1,
  pageSize: number = 10,
): Promise<ApiResponse<BlogPostsResponse>> {
  const query = new URLSearchParams();
  query.append("sort[0]", "publishedAt:desc");
  query.append("pagination[page]", String(page));
  query.append("pagination[pageSize]", String(pageSize));
  query.append("populate[0]", "coverImage");
  query.append("populate[1]", "author.avatar");
  query.append("populate[2]", "tags");

  const response = await callApi<StrapiBlogPostsResponse>(
    `${strapiUrl}/api/blog-posts?${query.toString()}`,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    },
  );

  if (!response.data) {
    return { data: undefined, error: response.error };
  }

  return {
    data: {
      items: response.data.data.map(mapStrapiPostToModel),
      total: response.data.meta.pagination.total,
      page: response.data.meta.pagination.page,
      pageSize: response.data.meta.pagination.pageSize,
      pageCount: response.data.meta.pagination.pageCount,
    },
    error: undefined,
  };
}

export async function getBlogPostBySlug(
  slug: string,
): Promise<ApiResponse<BlogPost | null>> {
  const query = new URLSearchParams();
  query.append("filters[slug][$eq]", slug);
  query.append("populate[0]", "coverImage");
  query.append("populate[1]", "author.avatar");
  query.append("populate[2]", "tags");
  query.append("populate[3]", "faq");
  query.append("pagination[pageSize]", "1");

  const response = await callApi<StrapiBlogPostsResponse>(
    `${strapiUrl}/api/blog-posts?${query.toString()}`,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    },
  );

  if (!response.data) {
    return { data: undefined, error: response.error };
  }

  const firstPost = response.data.data[0];
  return {
    data: firstPost ? mapStrapiPostToModel(firstPost) : null,
    error: undefined,
  };
}
